<template>
  <div v-if="showStockStatus" class="stock-status">
    <div class="stock-status__status" :class="stockStatusClass"></div>
    <div class="stock-status__message">
      <span>{{ stockStatusMessage }}</span>
      <footnote-reference v-if="stockStatusFootnoteText" :text="stockStatusFootnoteText" :reference="reference" />
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from 'vue';
import { useFootnoteReference } from '../../composables/footnote-reference.composable';
import { useI18n } from '../../composables/i18n.composable';
import { isEstimatedArrivalDateValid, isInStock } from '../../composables/stock-status.composable';
import { infuse } from '../../ioc-config';
import { StockType } from '../../models/vehicle.model';
import FootnoteReference from '../footnotes/footnoteReference.vue';

export default defineComponent({
  name: 'StockStatus',
  components: {
    FootnoteReference,
  },

  props: {
    /**
     * Availability of vehicles
     */
    stockType: {
      type: String as PropType<StockType>,
      default: StockType.IN_STOCK,
    },
    estimatedArrivalDate: {
      type: String,
      required: false,
      default: undefined,
    },
  },
  setup(props) {
    const { showStockStatus, locale } = infuse('config');
    const { t, exists } = useI18n();

    const { generateReference } = useFootnoteReference('stockStatus');
    const reference = generateReference();
    const stockStatusClass = computed<string>(() => (isInStock(props.stockType) ? '-green' : '-yellow'));
    const stockStatusMessage = computed<string>(() => {
      if (isInStock(props.stockType)) {
        return t('dcp.stockType.inStock');
      } else if (!isEstimatedArrivalDateValid(props.estimatedArrivalDate)) {
        return t('dcp.stockType.comingSoon');
      } else {
        const enMonthName =
          props.estimatedArrivalDate &&
          new Date(props.estimatedArrivalDate).toLocaleDateString('en-US', { month: 'long' });

        const localeMonthName =
          props.estimatedArrivalDate &&
          new Date(props.estimatedArrivalDate).toLocaleDateString(locale, { month: 'long' });

        const specificTranslationKey = `dcp.stockType.comingIn${enMonthName}`;

        return exists(specificTranslationKey, { detectEmpty: true })
          ? t(specificTranslationKey)
          : t('dcp.stockType.comingIn', { month: localeMonthName });
      }
    });

    const stockStatusFootnoteText = computed(() => {
      const translationKey = isInStock(props.stockType)
        ? 'dcp.stockType.footnote.inStock'
        : 'dcp.stockType.footnote.comingIn';

      return exists(translationKey, { detectEmpty: true }) && t(translationKey);
    });

    return {
      showStockStatus,
      stockStatusClass,
      stockStatusMessage,
      stockStatusFootnoteText,
      reference,
    };
  },
});
</script>

<style src="./stockStatus.scss" lang="scss"></style>
